import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { CgmConnectionStep, CgmDisplayStatus } from "src/constants/cgm";
import {
  isCgmConnectionJourneyStep,
  sensorStateToDisplayStatus,
} from "src/constants/cgm";
import type {
  CgmState,
  CgmDailyLog,
  CgmCalendarData,
  PocDeviceJson,
  CgmDeviceFoundPayload,
  PocGlucoseJson,
  CgmHardSyncDisplayData,
  CgmLiveReadingData,
  CgmConnectionLiveSnapshot,
  CgmAndroidBackgroundSyncState,
  CgmStatusData,
} from "src/types/cgm.types";
import {
  getLatestReadingByTimeInMillis,
  sortReadingsByTimeInMillis,
} from "src/helpers/cgm";

const initialState: CgmState = {
  step: "intro",
  boundDevice: null,
  scannedDevices: [],
  warmupEndDate: null,
  latestReadings: [],
  currentGlucose: null,
  batteryPercent: null,
  isConnected: false,
  error: null,
  dailyLogs: { loading: false, data: null, error: null },

  cgmConnectionLive: null,

  displayStatus: "NOT_CONNECTED",
  sensorStateCode: null,
  sensorStatusName: null,
  cgmHardSync: { isLoading: false, data: null, error: null },
  cgmGetLatest: { isLoading: false, error: null },
  cgmMissingSync: { isLoading: false, error: null },
  cgmAndroidBackgroundSync: null,
  cgmRemoteRefreshNonce: 0,
  statusAndSensorLife: { loading: false, data: null, error: null },
  cgmLiveReading: { loading: false, data: null, error: null },
  cgmCalendar: { loading: false, data: null, error: null },
  cgmCumulativeLogs: { loading: false, data: null, error: null },
};

const cgmSlice = createSlice({
  name: "cgm",
  initialState,
  reducers: {
    setStep(state, action: PayloadAction<CgmConnectionStep>) {
      state.step = action.payload;
      if (action.payload !== "error") state.error = null;
    },

    deviceFound(state, action: PayloadAction<CgmDeviceFoundPayload>) {
      const exists = state.scannedDevices.some(
        (d) => d.pocDevice.address === action.payload.pocDevice.address
      );
      if (!exists) {
        state.scannedDevices.push(action.payload);
      }
    },

    clearScannedDevices(state) {
      state.scannedDevices = [];
    },

    bindSuccess(
      state,
      action: PayloadAction<{
        pocDevice: PocDeviceJson;
        initEndDate?: string | null;
      }>
    ) {
      console.log(
        "[CGM slice] bindSuccess → warmupEndDate:",
        action.payload.initEndDate,
        "| prev step:",
        state.step
      );
      state.boundDevice = action.payload.pocDevice;
      state.warmupEndDate = action.payload.initEndDate ?? null;
      state.error = null;
      if (action.payload.initEndDate) {
        state.displayStatus = "WARMUP";
        if (!isCgmConnectionJourneyStep(state.step)) {
          state.step = "warmup";
        }
      } else {
        state.displayStatus = "CONNECTED";
        if (!isCgmConnectionJourneyStep(state.step)) {
          state.step = "connected";
        }
      }
    },

    bindFailed(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.step = "error";
      state.displayStatus = "ERROR";
    },

    connected(state, action: PayloadAction<PocDeviceJson>) {
      state.boundDevice = action.payload;
      state.isConnected = true;
      state.step = "connected";
      state.displayStatus = "CONNECTED";
      state.error = null;
    },

    /**
     * Link loss / soft disconnect (BE `DISCONNECTED`) — same binding; may auto-reconnect or Reconnect.
     */
    disconnected(state) {
      state.isConnected = false;
      state.displayStatus = "DISCONNECTED";
      state.cgmAndroidBackgroundSync = null;
    },

    /**
     * Full transmitter disconnect (BE `TRANSMITTER_DISCONNECT`) — restart pairing journey.
     */
    transmitterDisconnected(state) {
      state.isConnected = false;
      state.displayStatus = "TRANSMITTER_DISCONNECT";
      state.cgmAndroidBackgroundSync = null;
    },

    warmupStarted(state, action: PayloadAction<string>) {
      console.log("[CGM slice] warmupStarted → warmupEndDate:", action.payload);
      state.warmupEndDate = action.payload;
      state.displayStatus = "WARMUP";
      if (!isCgmConnectionJourneyStep(state.step)) {
        state.step = "warmup";
      }
    },

    /**
     * Local warmup timer finished — clear warmup fields so useCgm / UI leave WARMUP.
     * (RN may still send warmup briefly after reconnect; state is already CONNECTED.)
     */
    warmupCompleted(state) {
      state.warmupEndDate = null;
      state.step = "connected";
      state.displayStatus = "CONNECTED";
    },

    glucoseDataReceived(
      state,
      action: PayloadAction<{
        readings: PocGlucoseJson[];
        batteryPercent?: number | null;
        connectionStatus?: string | null;
        sensorState?: number | null;
        sensorStateName?: string | null;
        isConnected?: boolean;
        boundDevice?: PocDeviceJson | null;
        warmupEndDate?: string | null;
      }>
    ) {
      const {
        readings,
        batteryPercent,
        connectionStatus,
        sensorState,
        sensorStateName,
        isConnected,
        boundDevice,
        warmupEndDate,
      } = action.payload;
      if (readings.length > 0) {
        const sorted = sortReadingsByTimeInMillis(readings);
        state.latestReadings = sorted;
        const latest = getLatestReadingByTimeInMillis(sorted);
        if (latest) {
          state.currentGlucose = latest.showGlucoseMG || latest.showGlucose;
        }
      }
      if (batteryPercent != null) {
        console.log("[CGM slice] batteryPercent updated:", batteryPercent);
        state.batteryPercent = batteryPercent;
      }
      if (
        connectionStatus != null ||
        sensorState != null ||
        sensorStateName != null ||
        isConnected !== undefined ||
        boundDevice !== undefined ||
        warmupEndDate !== undefined
      ) {
        const prev = state.cgmConnectionLive;
        state.cgmConnectionLive = {
          connectionStatus: connectionStatus ?? prev?.connectionStatus ?? null,
          readings:
            readings.length > 0
              ? sortReadingsByTimeInMillis(readings)
              : prev?.readings ?? [],
          sensorState: sensorState ?? prev?.sensorState ?? null,
          sensorStateName: sensorStateName ?? prev?.sensorStateName ?? null,
          isConnected: isConnected ?? prev?.isConnected ?? state.isConnected,
          boundDevice:
            boundDevice !== undefined ? boundDevice : prev?.boundDevice ?? null,
          warmupEndDate:
            warmupEndDate !== undefined
              ? warmupEndDate
              : prev?.warmupEndDate ?? null,
        };
      }
    },

    sensorStateChanged(
      state,
      action: PayloadAction<{
        sensorState: number;
        statusName?: string;
        connectionStatus?: string | null;
        readings?: PocGlucoseJson[];
        boundDevice?: PocDeviceJson | null;
        warmupEndDate?: string | null;
        isConnected?: boolean;
      }>
    ) {
      const {
        sensorState,
        statusName,
        connectionStatus,
        readings,
        boundDevice,
        warmupEndDate,
        isConnected,
      } = action.payload;
      state.sensorStateCode = sensorState;
      state.sensorStatusName = statusName ?? null;

      if (
        connectionStatus != null ||
        (readings != null && readings.length > 0) ||
        boundDevice !== undefined ||
        warmupEndDate !== undefined ||
        isConnected !== undefined
      ) {
        const prev = state.cgmConnectionLive;
        const sortedReadings =
          readings != null && readings.length > 0
            ? sortReadingsByTimeInMillis(readings)
            : prev?.readings ?? [];
        state.cgmConnectionLive = {
          connectionStatus: connectionStatus ?? prev?.connectionStatus ?? null,
          readings: sortedReadings,
          sensorState,
          sensorStateName: statusName ?? prev?.sensorStateName ?? null,
          isConnected: isConnected ?? state.isConnected,
          boundDevice:
            boundDevice !== undefined ? boundDevice : prev?.boundDevice ?? null,
          warmupEndDate:
            warmupEndDate !== undefined
              ? warmupEndDate
              : prev?.warmupEndDate ?? null,
        };
      }

      if (sensorState === 2) {
        const end = state.warmupEndDate
          ? new Date(state.warmupEndDate).getTime()
          : NaN;
        const timedWarmupActive =
          Number.isFinite(end) && end > Date.now();
        const shouldShowWarmupUi =
          timedWarmupActive || state.step === "warmup";
        if (!shouldShowWarmupUi) {
          console.log(
            "[CGM slice] sensorState 2 but warmup window cleared — connected"
          );
          state.displayStatus = "CONNECTED";
          if (!isCgmConnectionJourneyStep(state.step)) {
            state.step = "connected";
          }
          return;
        }
        console.log("[CGM slice] sensorState 2 → warmup");
        state.displayStatus = "WARMUP";
        if (!isCgmConnectionJourneyStep(state.step)) {
          state.step = "warmup";
        }
        return;
      }

      if (sensorState === 102) {
        state.isConnected = true;

        if (state.step === "warmup") {
          console.log(
            "[CGM slice] sensorState 102 during warmup — ignored, staying in warmup"
          );
          return;
        }

        console.log(
          "[CGM slice] sensorState 102 → CONNECTED (step was:",
          state.step,
          ")"
        );
        state.displayStatus = "CONNECTED";
        if (state.step === "binding") {
          state.step = "connected";
        }
        return;
      }

      const displayStatus = sensorStateToDisplayStatus(sensorState);
      if (displayStatus) {
        state.displayStatus = displayStatus;
      }

      if (sensorState === 107 || sensorState === 108 || sensorState === 115) {
        state.isConnected = false;
      }
    },

    cgmConnectionLiveFromRn(
      state,
      action: PayloadAction<CgmConnectionLiveSnapshot>
    ) {
      state.cgmConnectionLive = action.payload;
    },

    /**
     * Host Bluetooth adapter on/off/denied (RN → WebView). Keeps `cgmConnectionLive.connectionStatus`
     * aligned with `/cgm/connection` when Tracky omits sensorState.
     */
    cgmBluetoothRadioSnapshot(
      state,
      action: PayloadAction<{ radio: "on" | "off" | "denied" }>
    ) {
      const { radio } = action.payload;
      const prev = state.cgmConnectionLive;

      if (radio === "off") {
        state.cgmConnectionLive = {
          connectionStatus: "BLUETOOTH_OFF",
          readings: prev?.readings ?? [],
          sensorState: prev?.sensorState ?? state.sensorStateCode,
          sensorStateName: prev?.sensorStateName ?? state.sensorStatusName,
          isConnected: false,
          boundDevice: prev?.boundDevice ?? state.boundDevice,
          warmupEndDate: prev?.warmupEndDate ?? state.warmupEndDate,
        };
        state.isConnected = false;
        state.displayStatus = "BLUETOOTH_OFF";
        return;
      }

      if (radio === "denied") {
        state.cgmConnectionLive = {
          connectionStatus: "ERROR_COMMON",
          readings: prev?.readings ?? [],
          sensorState: prev?.sensorState ?? state.sensorStateCode,
          sensorStateName: prev?.sensorStateName ?? state.sensorStatusName,
          isConnected: false,
          boundDevice: prev?.boundDevice ?? state.boundDevice,
          warmupEndDate: prev?.warmupEndDate ?? state.warmupEndDate,
        };
        state.isConnected = false;
        state.displayStatus = "ERROR";
        return;
      }

      if (prev) {
        state.cgmConnectionLive = {
          ...prev,
          connectionStatus: null,
        };
      }
    },

    statusUpdate(
      state,
      action: PayloadAction<{
        status: string;
        pocDevice?: PocDeviceJson | null;
        initEndDate?: string | null;
      }>
    ) {
      const { status, pocDevice, initEndDate } = action.payload;
      if (pocDevice) state.boundDevice = pocDevice;

      // Preserve warmup: don't overwrite warmupEndDate with past/stale initEndDate when we're in warmup
      const currentlyInWarmup =
        state.step === "warmup" &&
        state.warmupEndDate &&
        new Date(state.warmupEndDate).getTime() > Date.now();
      if (initEndDate) {
        const newEndTime = new Date(initEndDate).getTime();
        const isFuture = !Number.isNaN(newEndTime) && newEndTime > Date.now();
        if (!currentlyInWarmup || isFuture) {
          state.warmupEndDate = initEndDate;
        }
      }

      console.log(
        "[CGM slice] statusUpdate →",
        status,
        "| initEndDate:",
        initEndDate,
        "| prev step:",
        state.step
      );

      switch (status) {
        case "bound":
          state.isConnected = true;
          if (
            state.step === "warmup" &&
            state.warmupEndDate &&
            new Date(state.warmupEndDate).getTime() > Date.now()
          ) {
            console.log(
              "[CGM slice] statusUpdate 'bound' during active warmup — staying in warmup"
            );
            break;
          }
          // RN may send "bound" instead of "warmup" after fresh bind — show warmup if initEndDate is in future
          if (initEndDate && new Date(initEndDate).getTime() > Date.now()) {
            console.log(
              "[CGM slice] statusUpdate 'bound' with future initEndDate → warmup"
            );
            state.displayStatus = "WARMUP";
            if (!isCgmConnectionJourneyStep(state.step)) {
              state.step = "warmup";
            }
            break;
          }
          state.displayStatus = "CONNECTED";
          if (!isCgmConnectionJourneyStep(state.step)) {
            state.step = "connected";
          }
          break;
        case "warmup": {
          const endIso = state.warmupEndDate ?? initEndDate ?? null;
          const endMs = endIso ? new Date(endIso).getTime() : NaN;
          const hasFutureWarmup =
            Number.isFinite(endMs) && endMs > Date.now();
          if (!hasFutureWarmup) {
            console.log(
              "[CGM slice] statusUpdate 'warmup' with no future end — connected"
            );
            state.displayStatus = "CONNECTED";
            if (!isCgmConnectionJourneyStep(state.step)) {
              state.step = "connected";
            }
            break;
          }
          state.displayStatus = "WARMUP";
          if (!isCgmConnectionJourneyStep(state.step)) {
            state.step = "warmup";
          }
          break;
        }
        case "no_device":
          state.step = "intro";
          state.isConnected = false;
          state.boundDevice = null;
          state.displayStatus = "NOT_CONNECTED";
          break;
        case "unbound":
          state.isConnected = false;
          state.boundDevice = null;
          state.step = "intro";
          state.displayStatus = "NOT_CONNECTED";
          break;
        /** RN `expireSensor` — sensor cycle ended; connect a new CGM (same as BE `EXPIRED`). */
        case "expired":
          state.isConnected = false;
          state.boundDevice = null;
          state.step = "intro";
          state.warmupEndDate = null;
          state.displayStatus = "EXPIRED";
          break;
      }
    },

    setDisplayStatus(state, action: PayloadAction<CgmDisplayStatus>) {
      state.displayStatus = action.payload;
    },

    cgmError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },

    resetCgm() {
      return initialState;
    },

    // Hard sync (web starts loading on send; RN: SUCCESS / ERROR only)
    cgmHardSyncStarted(state) {
      state.cgmHardSync.isLoading = true;
      state.cgmHardSync.data = null;
      state.cgmHardSync.error = null;
      state.displayStatus = "SYNCING";
    },
    cgmGetLatestStarted(state) {
      state.cgmGetLatest.isLoading = true;
      state.cgmGetLatest.error = null;
    },
    cgmGetLatestSuccess(state) {
      state.cgmGetLatest.isLoading = false;
      state.cgmGetLatest.error = null;
    },
    cgmGetLatestError(state, action: PayloadAction<{ error: string }>) {
      state.cgmGetLatest.isLoading = false;
      state.cgmGetLatest.error = action.payload.error;
    },
    cgmMissingSyncStarted(state) {
      state.cgmMissingSync.isLoading = true;
      state.cgmMissingSync.error = null;
    },
    cgmMissingSyncSuccess(state) {
      state.cgmMissingSync.isLoading = false;
      state.cgmMissingSync.error = null;
    },
    cgmMissingSyncError(state, action: PayloadAction<{ error: string }>) {
      state.cgmMissingSync.isLoading = false;
      state.cgmMissingSync.error = action.payload.error;
    },
    cgmHardSyncSuccess(
      state,
      action: PayloadAction<{ result: CgmHardSyncDisplayData }>
    ) {
      state.cgmHardSync.isLoading = false;
      state.cgmHardSync.data = action.payload.result;
      state.cgmHardSync.error = null;
      if (state.displayStatus === "SYNCING") {
        state.displayStatus = state.isConnected ? "CONNECTED" : "DISCONNECTED";
      }
    },
    cgmHardSyncError(state, action: PayloadAction<{ error: string }>) {
      state.cgmHardSync.isLoading = false;
      state.cgmHardSync.data = null;
      state.cgmHardSync.error = action.payload.error;
      if (state.displayStatus === "SYNCING") {
        state.displayStatus = state.isConnected ? "CONNECTED" : "DISCONNECTED";
      }
    },

    cgmAndroidBackgroundSyncStatus(
      state,
      action: PayloadAction<CgmAndroidBackgroundSyncState>
    ) {
      state.cgmAndroidBackgroundSync = action.payload;
    },

    bumpCgmRemoteRefreshNonce(state) {
      state.cgmRemoteRefreshNonce += 1;
    },

    // API-driven state
    getCgmStatusRequest(state) {
      state.statusAndSensorLife.loading = true;
      state.statusAndSensorLife.error = null;
    },
    getCgmStatusSuccess(
      state,
      action: PayloadAction<{ data: Array<CgmStatusData> }>
    ) {
      state.statusAndSensorLife.loading = false;
      const data = action.payload.data as Array<CgmStatusData>;
      state.statusAndSensorLife.data = data;
      const row = data?.[0];
      if (!row) {
        return;
      }
      const endMs = row.endDate ? new Date(row.endDate).getTime() : NaN;
      const expiredByDate =
        !Number.isNaN(endMs) && endMs < Date.now();
      if (row.status === "EXPIRED" || expiredByDate) {
        state.displayStatus = "EXPIRED";
        return;
      }
      const warmupEndMs = state.warmupEndDate
        ? new Date(state.warmupEndDate).getTime()
        : NaN;
      const localWarmupWindowActive =
        Number.isFinite(warmupEndMs) && warmupEndMs > Date.now();
      if (localWarmupWindowActive) {
        return;
      }
      const inWarmupUi =
        state.step === "warmup" || state.displayStatus === "WARMUP";
      if (inWarmupUi) {
        return;
      }
      if (row.status === "DISCONNECTED") {
        state.displayStatus = "DISCONNECTED";
      } else if (row.status === "TRANSMITTER_DISCONNECT") {
        state.displayStatus = "TRANSMITTER_DISCONNECT";
      } else if (row.status === "CONNECTED") {
        state.displayStatus = "CONNECTED";
      }
    },
    getCgmStatusError(state, action: PayloadAction<string>) {
      state.statusAndSensorLife.loading = false;
      state.statusAndSensorLife.error = action.payload as string;
    },

    // get live reading
    getLiveReadingRequest(state) {
      state.cgmLiveReading.loading = true;
      state.cgmLiveReading.data = null;
      state.cgmLiveReading.error = null;
    },
    getLiveReadingSuccess(
      state,
      action: PayloadAction<{ data: CgmLiveReadingData }>
    ) {
      state.cgmLiveReading.loading = false;
      state.cgmLiveReading.data = action.payload.data;
    },
    getLiveReadingError(state, action: PayloadAction<string>) {
      state.cgmLiveReading.loading = false;
      state.cgmLiveReading.data = null;
      state.cgmLiveReading.error = action.payload;
    },

    getDailyLogsRequest(state, _action: PayloadAction<{ startDate: string }>) {
      state.dailyLogs.loading = true;
      state.dailyLogs.error = null;
    },

    getDailyLogsSuccess(
      state,
      action: PayloadAction<{ data: CgmDailyLog | null }>
    ) {
      state.dailyLogs.loading = false;
      state.dailyLogs.error = null;
      state.dailyLogs.data = action.payload.data;
    },

    getDailyLogsError(state, action: PayloadAction<string>) {
      state.dailyLogs.loading = false;
      state.dailyLogs.error = action.payload;
    },

    getCGMCalendarRequest(
      state,
      _action: PayloadAction<{ month: number; year: number }>
    ) {
      state.cgmCalendar.loading = true;
      state.cgmCalendar.data = null;
      state.cgmCalendar.error = null;
    },
    getCGMCalendarSuccess(
      state,
      action: PayloadAction<{ data: CgmCalendarData | null }>
    ) {
      state.cgmCalendar.loading = false;
      state.cgmCalendar.data = action.payload.data;
      state.cgmCalendar.error = null;
    },
    getCGMCalendarError(state, action: PayloadAction<string>) {
      state.cgmCalendar.loading = false;
      state.cgmCalendar.data = null;
      state.cgmCalendar.error = action.payload;
    },

    getCGMCumulativeLogsRequest(
      state,
      _action: PayloadAction<{ startDate: string; endDate: string }>
    ) {
      state.cgmCumulativeLogs.loading = true;
      state.cgmCumulativeLogs.data = null;
      state.cgmCumulativeLogs.error = null;
    },
    getCGMCumulativeLogsSuccess(
      state,
      action: PayloadAction<{ data: Record<string, unknown> | null }>
    ) {
      state.cgmCumulativeLogs.loading = false;
      state.cgmCumulativeLogs.data = action.payload.data;
      state.cgmCumulativeLogs.error = null;
    },
    getCGMCumulativeLogsError(state, action: PayloadAction<string>) {
      state.cgmCumulativeLogs.loading = false;
      state.cgmCumulativeLogs.data = null;
      state.cgmCumulativeLogs.error = action.payload;
    },
  },
});

export const {
  setStep,
  deviceFound,
  clearScannedDevices,
  bindSuccess,
  bindFailed,
  connected,
  disconnected,
  transmitterDisconnected,
  warmupStarted,
  warmupCompleted,
  glucoseDataReceived,
  sensorStateChanged,
  cgmConnectionLiveFromRn,
  cgmBluetoothRadioSnapshot,
  statusUpdate,
  setDisplayStatus,
  cgmError,
  resetCgm,
  cgmHardSyncStarted,
  cgmHardSyncSuccess,
  cgmHardSyncError,
  cgmGetLatestStarted,
  cgmGetLatestSuccess,
  cgmGetLatestError,
  cgmMissingSyncStarted,
  cgmMissingSyncSuccess,
  cgmMissingSyncError,
  cgmAndroidBackgroundSyncStatus,
  bumpCgmRemoteRefreshNonce,

  // get status
  getCgmStatusRequest,
  getCgmStatusSuccess,
  getCgmStatusError,

  // get live reading
  getLiveReadingRequest,
  getLiveReadingSuccess,
  getLiveReadingError,

  // get daily logs
  getDailyLogsRequest,
  getDailyLogsSuccess,
  getDailyLogsError,

  // get calendar
  getCGMCalendarRequest,
  getCGMCalendarSuccess,
  getCGMCalendarError,

  // get cumulative logs
  getCGMCumulativeLogsRequest,
  getCGMCumulativeLogsSuccess,
  getCGMCumulativeLogsError,
} = cgmSlice.actions;

export default cgmSlice.reducer;
